/**
 * 리스크(EA-41) — 탭 「이상거래 큐」·「규칙」, 규칙 값 바꾸기(EA-49), 이상거래 항목 상세(EA-50).
 *
 * 레거시가 비워 둔 자리(`/risk` pending — 「Risk 백엔드가 아직 없다」)에 만든다. 그 까닭은
 * 없어졌다: 계약 XM-62~XM-68 이 모두 '있음'이고, 거래소 01 9-1 30번(확정 2026-09-24)
 * '임시값으로 켜 두고 관리자가 확인한다. 수치는 추후 관리자가 거래소관리자에서 정한다'.
 *
 * ⚠ **표시·기록만이다.** 이상거래 항목을 바꿔도 잔고·등급·계정·에스크로는 움직이지 않는다
 * (거래소 01 6-22).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  evaluateMemberRisk, getMemberRisk, getRiskQueueItem, listRiskQueue, listRiskRules,
  patchRiskQueueItem, patchRiskRule,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  AdminRef, Badge, Button, Card, Code, Field, Labeled, LINK, Maybe, MemberLink, Notice, Row, Select,
  Tabs, TD, TextArea, TH, Time,
} from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { PagePager } from '@/components/Pager'
import { Choice, FilterBar, idParam, optionsOf, Search, useFilters } from '@/components/Filters'
import {
  RISK_LEVEL_LABEL, RISK_QUEUE_STATUS_LABEL, RISK_SCOPE_LABEL,
  type RiskLevel, type RiskQueueItem, type RiskQueuePatch, type RiskQueueStatus, type RiskRule,
  type RiskRulePatch,
} from '@/api/types'

const SIZE = 25

/** 위험 단계 색 — 주의 노랑·의심 주황·고위험 빨강(제안 03 3번). */
function LevelTag({ level }: { level: string | null | undefined }) {
  const color = level === 'HIGH_RISK'
    ? 'text-red-700 dark:text-red-400'
    : level === 'SUSPICIOUS' ? 'text-orange-600 dark:text-orange-400'
      : level === 'CAUTION' ? 'text-amber-600 dark:text-amber-400' : ''
  return <span className={`font-medium ${color}`}><Labeled labels={RISK_LEVEL_LABEL} code={level} /></span>
}

/** 이상거래 항목이 끝났는가 — 해결·오탐(서버 `RiskQueue.FINAL`). */
function isFinal(status: string): boolean {
  return status === 'RESOLVED' || status === 'FALSE_POSITIVE'
}

/* ── EA-41 리스크 화면 · 탭 ──────────────────────────────────────── */

type Tab = 'queue' | 'rules'
const NO_FILTERS: readonly string[] = []

export function RiskPage() {
  const { has } = useSession()
  const f = useFilters(NO_FILTERS)
  const tabs: { kind: Tab; label: string }[] = []
  if (has('risk_queue.view')) tabs.push({ kind: 'queue', label: 'Suspicious trade queue' })
  if (has('risk.view')) tabs.push({ kind: 'rules', label: 'Rules' })
  const [active, setActive] = useState<Tab>(tabs[0]?.kind ?? 'queue')

  return (
    <Card title="Risk">
      <p className="mb-4 text-xs text-slate-500">
        Suspicious trade items the server flagged by rules, and those rules. The rules are switched on with
        temporary values. They only show and record; they do not touch balances, grades, accounts or escrow.
      </p>
      <Tabs tabs={tabs} active={active} onSelect={(k) => {
        if (k === active) return
        f.resetPaging()
        setActive(k)
      }} />
      {active === 'queue' && <QueueTab />}
      {active === 'rules' && <RulesTab />}
    </Card>
  )
}

/* ── 이상거래 큐 (XM-66) ─────────────────────────────────────────── */

const QUEUE_FILTERS = ['status', 'level', 'memberId', 'assigneeAdminId'] as const

function QueueTab() {
  const f = useFilters(QUEUE_FILTERS)
  const params = {
    status: f.values['status'] || undefined,
    level: f.values['level'] || undefined,
    memberId: idParam(f.values['memberId']),
    assigneeAdminId: idParam(f.values['assigneeAdminId']),
    page: f.page,
    size: SIZE,
  }
  const q = useQuery({ queryKey: ['risk-queue', params], queryFn: () => listRiskQueue(params) })
  const rows = q.data?.items ?? []

  return (
    <>
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Status" value={f.values['status'] ?? ''}
          options={optionsOf(RISK_QUEUE_STATUS_LABEL)} onChange={(v) => f.set('status', v)} />
        <Choice label="Level" value={f.values['level'] ?? ''}
          options={optionsOf(RISK_LEVEL_LABEL)} onChange={(v) => f.set('level', v)} />
        <Search label="Member no." value={f.values['memberId'] ?? ''} inputMode="numeric"
          onChange={(v) => f.set('memberId', v)} />
        <Search label="Assignee admin no." value={f.values['assigneeAdminId'] ?? ''} inputMode="numeric"
          onChange={(v) => f.set('assigneeAdminId', v)} />
      </FilterBar>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="No items match the filters.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Member</th><th className={TH}>Rule</th>
            <th className={TH}>Level</th><th className={TH}>Status</th><th className={TH}>Assignee</th>
            <th className={TH}>Related trade</th><th className={TH}>Related incident</th><th className={TH}>Hits</th>
            <th className={TH}>Last signal</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className={TD}><Link className={LINK} to={`/risk/queue/${r.id}`}>{r.id}</Link></td>
                <td className={TD}><MemberLink memberId={r.memberId} loginId={r.memberLoginId} /></td>
                <td className={TD}><Code>{r.ruleCode}</Code></td>
                <td className={TD}><LevelTag level={r.level} /></td>
                <td className={TD}><Labeled labels={RISK_QUEUE_STATUS_LABEL} code={r.status} /></td>
                <td className={TD}><Maybe value={r.assigneeLoginId} /></td>
                <td className={`${TD} font-mono text-xs`}><Maybe value={r.relatedOrderId} /></td>
                <td className={TD}><Maybe value={r.relatedIncidentId} /></td>
                <td className={TD}>{r.hits}</td>
                <td className={TD}><Time ms={r.lastHitAt} /></td>
                <td className={TD}><Time ms={r.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <PagePager page={f.page} size={SIZE} total={q.data?.total} onChange={f.setPage} withPrev />
    </>
  )
}

/* ── 규칙 (XM-62) ────────────────────────────────────────────────── */

function RulesTab() {
  const { has } = useSession()
  const q = useQuery({ queryKey: ['risk-rules'], queryFn: listRiskRules })
  const [editing, setEditing] = useState<RiskRule | null>(null)
  const rows = q.data ?? []
  const canEdit = has('risk.rules.edit')

  return (
    <>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no rules.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Code</th><th className={TH}>Signal</th><th className={TH}>Scope</th>
            <th className={TH}>Period</th><th className={TH}>Threshold</th><th className={TH}>Level</th>
            <th className={TH}>Enabled</th><th className={TH}>Description</th><th className={TH}>Source</th>
            <th className={TH}>Changed at</th><th className={TH}>Changed by</th>
            {canEdit && <th className={TH}>Actions</th>}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.code}>
                <td className={TD}>
                  <Code>{r.code}</Code>
                  {r.temporary && <Badge tone="warn">Temporary value</Badge>}
                </td>
                <td className={TD}><Code>{r.signal}</Code></td>
                <td className={TD}><Labeled labels={RISK_SCOPE_LABEL} code={r.scope} /></td>
                <td className={TD}><Maybe value={r.windowDays} unit=" days" /></td>
                <td className={TD}>{r.threshold}{r.thresholdUnit ? ` ${r.thresholdUnit}` : ''}</td>
                <td className={TD}><LevelTag level={r.level} /></td>
                <td className={TD}>{r.enabled ? 'On' : 'Off'}</td>
                <td className={TD}><Maybe value={r.description} /></td>
                <td className={TD}><Maybe value={r.source} /></td>
                <td className={TD}><Time ms={r.updatedAt} /></td>
                <td className={TD}><AdminRef adminId={r.updatedByAdminId} /></td>
                {canEdit && (
                  <td className={TD}><Button tone="plain" onClick={() => setEditing(r)}>Change values</Button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      {editing && <RuleDialog rule={editing} onClose={() => setEditing(null)} />}
    </>
  )
}

/** 1 이상 정수 글 → 숫자, 아니면 null. (금액이 아니다 — 횟수·일수다.) */
function positiveInt(v: string, max?: number): number | null {
  if (!/^\d{1,15}$/.test(v.trim())) return null
  const n = Number(v.trim())
  if (n < 1 || (max !== undefined && n > max)) return null
  return n
}

/** EA-49 — 규칙 값 바꾸기(XM-63, 바뀐 칸만). */
function RuleDialog({ rule, onClose }: { rule: RiskRule; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const windowed = rule.scope === 'WINDOW_DAYS'
  const [windowDays, setWindowDays] = useState(rule.windowDays === null ? '' : String(rule.windowDays))
  const [threshold, setThreshold] = useState(String(rule.threshold))
  const [level, setLevel] = useState<RiskLevel>(rule.level)
  const [enabled, setEnabled] = useState(rule.enabled)

  const patch: RiskRulePatch = {}
  let invalid = false
  if (windowed && windowDays.trim() !== (rule.windowDays === null ? '' : String(rule.windowDays))) {
    const n = positiveInt(windowDays, 3650)
    if (n === null) invalid = true
    else patch.windowDays = n
  }
  if (threshold.trim() !== String(rule.threshold)) {
    const n = positiveInt(threshold)
    if (n === null) invalid = true
    else patch.threshold = n
  }
  if (level !== rule.level) patch.level = level
  if (enabled !== rule.enabled) patch.enabled = enabled
  const changed = Object.keys(patch).length > 0

  async function submit() {
    const body = { ...patch }
    const send = async () => {
      await patchRiskRule(rule.code, body)
      setFlash({ tone: 'ok', text: `Changed rule ${rule.code}.` })
      await qc.invalidateQueries({ queryKey: ['risk-rules'] })
    }
    await submitWrite(w, 'Change risk rule', send, onClose)
  }

  return (
    <Confirm open title={`Rule ${rule.code}`} action="Apply"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={!changed || invalid}>
      <div className="flex flex-col gap-3">
        {rule.description && <p>{rule.description}</p>}
        {windowed && (
          <Field label="Period (days)" value={windowDays} inputMode="numeric"
            onChange={(e) => setWindowDays(e.target.value)} />
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Threshold</span>
          <span className="flex items-center gap-2">
            <input value={threshold} inputMode="numeric" onChange={(e) => setThreshold(e.target.value)}
              aria-label="Threshold"
              className="rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800" />
            {rule.thresholdUnit && <Code>{rule.thresholdUnit}</Code>}
          </span>
        </label>
        <Select label="Level" value={level} onChange={(v) => setLevel(v as RiskLevel)}
          options={optionsOf(RISK_LEVEL_LABEL)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Enabled</span>
        </label>
        {invalid && (
          <p className="text-xs text-red-700 dark:text-red-400">
            {windowed ? 'The period is 1–3650 and the threshold' : 'The threshold'} is a whole number of 1 or more.
          </p>
        )}
        <p className="text-xs text-slate-500">Changes are recorded in the audit log with the values before and after.</p>
      </div>
    </Confirm>
  )
}

/* ── EA-50 이상거래 항목 상세 (XM-67 · XM-68 · XM-64 · XM-65) ─────── */

export function RiskQueueItemPage() {
  const { id: raw = '' } = useParams()
  const id = Number(raw)
  const { has } = useSession()
  const q = useQuery({ queryKey: ['risk-item', id], queryFn: () => getRiskQueueItem(id) })
  const item = q.data?.item

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/risk" className="text-xs text-slate-500 underline">← Suspicious trade queue</Link>
      </div>
      <Card title={`Item #${raw}`}>
        <QueryState isPending={q.isPending} error={q.error}>
          {item && (
            <dl className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
              <Row label="Member"><MemberLink memberId={item.memberId} loginId={item.memberLoginId} /></Row>
              <Row label="Rule"><Code>{item.ruleCode}</Code></Row>
              <Row label="Level"><LevelTag level={item.level} /></Row>
              <Row label="Status"><Labeled labels={RISK_QUEUE_STATUS_LABEL} code={item.status} /></Row>
              <Row label="Assignee"><AdminRef adminId={item.assigneeAdminId} loginId={item.assigneeLoginId} /></Row>
              <Row label="Evidence">
                <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-100 p-2 font-mono text-xs dark:bg-slate-800">
                  {item.evidence === null || item.evidence === undefined ? '—' : JSON.stringify(item.evidence, null, 2)}
                </pre>
              </Row>
              <Row label="Related trade"><span className="font-mono text-xs"><Maybe value={item.relatedOrderId} /></span></Row>
              <Row label="Related incident">
                {item.relatedIncidentId === null
                  ? <Maybe value={null} />
                  : <Link className={LINK} to={`/incidents/${item.relatedIncidentId}`}>#{item.relatedIncidentId}</Link>}
              </Row>
              <Row label="Hits">{item.hits}</Row>
              <Row label="Created at"><Time ms={item.createdAt} /></Row>
              <Row label="Last signal"><Time ms={item.lastHitAt} /></Row>
            </dl>
          )}
        </QueryState>
      </Card>

      {q.data && (
        <Card title="Action history">
          {q.data.events.length === 0
            ? <p className="text-sm text-slate-500">No action history.</p>
            : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className={TH}>Time</th><th className={TH}>Admin</th><th className={TH}>Status</th>
                  <th className={TH}>Assignee</th><th className={TH}>Incident</th><th className={TH}>Note</th>
                </tr></thead>
                <tbody>
                  {q.data.events.map((e) => (
                    <tr key={e.id}>
                      <td className={TD}><Time ms={e.at} /></td>
                      <td className={TD}><AdminRef adminId={e.adminId} loginId={e.adminLoginId} /></td>
                      <td className={TD}>
                        <Labeled labels={RISK_QUEUE_STATUS_LABEL} code={e.fromStatus} /> →{' '}
                        <Labeled labels={RISK_QUEUE_STATUS_LABEL} code={e.toStatus} />
                      </td>
                      <td className={TD}><AdminRef adminId={e.assigneeAdminId} /></td>
                      <td className={TD}><Maybe value={e.incidentId} /></td>
                      <td className={`${TD} whitespace-pre-wrap`}><Maybe value={e.note} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      )}

      {item && has('risk.view') && <MemberRiskCard memberId={item.memberId} />}
      {item && has('risk_queue.edit') && !isFinal(item.status) && <QueueItemForm item={item} />}
    </div>
  )
}

/** 회원 위험(XM-64) + 「다시 평가」(XM-65, `risk.evaluate`). */
function MemberRiskCard({ memberId }: { memberId: number }) {
  const { has } = useSession()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['member-risk', memberId], queryFn: () => getMemberRisk(memberId) })
  const w = useWrite()

  async function evaluate() {
    const send = async () => {
      const r = await evaluateMemberRisk(memberId)
      setFlash({ tone: 'ok', text: `${r.createdItemIds.length} new items` })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['member-risk', memberId] }),
        qc.invalidateQueries({ queryKey: ['risk-queue'] }),
      ])
    }
    await submitWrite(w, 'Re-evaluate member risk', send, () => undefined)
  }

  return (
    <Card title="Member risk"
      actions={has('risk.evaluate')
        ? <Button tone="plain" onClick={() => { void evaluate() }} disabled={w.busy}>
            {w.busy ? 'Evaluating…' : 'Re-evaluate'}
          </Button>
        : undefined}>
      {w.error && <div className="mb-3"><Notice tone="stop">{w.error}</Notice></div>}
      <QueryState isPending={q.isPending} error={q.error}>
        {q.data && (
          <dl className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
            <Row label="Member risk level"><LevelTag level={q.data.level} /></Row>
            <Row label="Risk items">{q.data.activeItems.length}</Row>
            <Row label="Count by rule">
              {Object.keys(q.data.counts).length === 0
                ? <Maybe value={null} />
                : (
                  <span className="flex flex-wrap gap-2">
                    {Object.entries(q.data.counts).map(([code, n]) => (
                      <span key={code}><Code>{code}</Code> {n}</span>
                    ))}
                  </span>
                )}
            </Row>
          </dl>
        )}
      </QueryState>
    </Card>
  )
}

/** 처리 칸(XM-68, 바뀐 칸만) — 상태·담당·관련 사고·메모. */
function QueueItemForm({ item }: { item: RiskQueueItem }) {
  const { me } = useSession()
  const qc = useQueryClient()
  const w = useWrite()
  const [status, setStatus] = useState<RiskQueueStatus>(item.status)
  const [assignee, setAssignee] = useState(item.assigneeAdminId === null ? '' : String(item.assigneeAdminId))
  const [incident, setIncident] = useState(item.relatedIncidentId === null ? '' : String(item.relatedIncidentId))
  const [note, setNote] = useState('')

  const patch: RiskQueuePatch = {}
  let invalid = false
  if (status !== item.status) patch.status = status
  if (assignee.trim() !== (item.assigneeAdminId === null ? '' : String(item.assigneeAdminId))) {
    if (/^\d+$/.test(assignee.trim())) patch.assigneeAdminId = Number(assignee.trim())
    else invalid = true
  }
  if (incident.trim() !== (item.relatedIncidentId === null ? '' : String(item.relatedIncidentId))) {
    if (/^\d+$/.test(incident.trim())) patch.relatedIncidentId = Number(incident.trim())
    else invalid = true
  }
  if (note.trim() !== '') patch.note = note.trim()
  const changed = Object.keys(patch).length > 0

  async function apply() {
    const body = { ...patch }
    const send = async () => {
      await patchRiskQueueItem(item.id, body)
      setFlash({ tone: 'ok', text: `Updated item #${item.id}.` })
      setNote('')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['risk-item', item.id] }),
        qc.invalidateQueries({ queryKey: ['risk-queue'] }),
      ])
    }
    await submitWrite(w, 'Update suspicious trade item', send, () => undefined)
  }

  return (
    <Card title="Actions">
      <div className="flex max-w-md flex-col gap-3">
        <Select label="Status" value={status} onChange={(v) => setStatus(v as RiskQueueStatus)}
          options={optionsOf(RISK_QUEUE_STATUS_LABEL)} />
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Field label="Assignee admin no." value={assignee} inputMode="numeric"
              onChange={(e) => setAssignee(e.target.value)} />
          </div>
          {me && (
            <Button tone="plain" onClick={() => setAssignee(String(me.adminId))}>Assign to me</Button>
          )}
        </div>
        <Field label="Related incident no." value={incident} inputMode="numeric"
          onChange={(e) => setIncident(e.target.value)} />
        <TextArea label="Note" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
        {invalid && <p className="text-xs text-red-700 dark:text-red-400">Write the numbers in digits.</p>}
        {w.error && <Notice tone="stop">{w.error}</Notice>}
        <div>
          <Button onClick={() => { void apply() }} disabled={w.busy || !changed || invalid}>
            {w.busy ? 'Processing…' : 'Apply'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

